import { useCallback } from 'react';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  LabelValueDetailsInput,
  TimeSignalClassification,
  UpdateTimeSignalsInput,
  UpdateTimeSignalsMutation,
  useUpdateTimeSignalsMutation,
  TimeSignalFilterInput,
  TimeSignal
} from 'graphql/types';
import { useMeContext } from 'modules/me/MeContextProvider';
import { useNotesToastContext } from '../NotesToastContextProvider';
import {
  NotesClassification,
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { TIMELINE_TIMESIGNAL_COUNT } from '../Timeline';
import { updateTimeSignalsCache } from '../cache/utils/cache-utils';
import { truncateMessage } from '../common/utils';
import { clearCachedDates } from '../cache/utils/time-signal-utils';
import {
  useTimeSignalGetParam,
  TimeSignalGetParam,
  useTimeSignalUpdateFilter,
  UpdateFilterParams
} from './useTimeSignalParam';

export const updateCache = (
  param: TimeSignalGetParam,
  selectedClassification: NotesClassification,
  classificationTarget: TimeSignalClassification
) => (
  cache: ApolloCache<UpdateTimeSignalsMutation>,
  mutationResult: FetchResult<
    UpdateTimeSignalsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.updateTimeSignals?.items) {
    updateTimeSignalsCache(
      cache,
      mutationResult?.data?.updateTimeSignals?.items as TimeSignal[],
      param,
      selectedClassification,
      classificationTarget
    );
  }
};

type BulkUpdateClassificationArgs = {
  selectedNoteIds: Array<string>;
  unSelectedNoteIds: Array<string>;
  classificationTarget: TimeSignalClassification;
  note?: TimeSignal;
};

type BulkUpdateArgs = {
  selectedNoteIds: Array<string>;
  unSelectedNoteIds: Array<string>;
  classificationTarget: TimeSignalClassification;
  labelValues?: LabelValueDetailsInput[];
};

const getFilterInput = ({
  selectedNoteIds,
  unSelectedNoteIds,
  classificationTarget,
  labelValues,
  cursor,
  selectedFilterFn
}: BulkUpdateArgs & {
  cursor: string | null;
  selectedFilterFn: (params: UpdateFilterParams) => TimeSignalFilterInput;
}) => {
  const selectionFilter = selectedFilterFn({
    includeIds: selectedNoteIds.length ? selectedNoteIds : undefined,
    excludeIds: unSelectedNoteIds.length ? unSelectedNoteIds : undefined
  });

  const filterInput: UpdateTimeSignalsInput = {
    selectionFilter,
    updates: {
      classification: classificationTarget,
      labelValues
    },
    cursor
  };
  return filterInput;
};

export const getMoveMessage = (
  classificationTarget: TimeSignalClassification,
  note?: TimeSignal
): string =>
  note
    ? `toast.note.classification.singleMove.${classificationTarget.toLocaleLowerCase()}`
    : `toast.note.classification.bulkMove.${classificationTarget.toLocaleLowerCase()}`;

export const useBulkUpdateTimeSignals = (): {
  onTimeSignalClassificationUpdate: (
    args: BulkUpdateClassificationArgs
  ) => Promise<void>;
  onMultiEditTimeSignals: (args: BulkUpdateArgs) => Promise<void>;
  updateInProgress: boolean;
} => {
  const selectedFilterFn = useTimeSignalUpdateFilter();
  const {
    filterContext: { pageType }
  } = useNotesFilterContext();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );

  const [
    bulkUpdateTimeSignals,
    { loading, error }
  ] = useUpdateTimeSignalsMutation();
  const { setToastContext } = useNotesToastContext();
  const {
    filterContext: { selectedClassification }
  } = useNotesFilterContext();
  const { id } = useMeContext() ?? {};
  const onTimeSignalClassificationUpdate = useCallback(
    async (args: BulkUpdateClassificationArgs) => {
      const { selectedNoteIds, classificationTarget } = args;
      let result = null;
      clearCachedDates(id);
      do {
        const input = getFilterInput({
          ...args,
          cursor: result?.data?.updateTimeSignals.cursor ?? null,
          selectedFilterFn
        });

        try {
          result = await bulkUpdateTimeSignals({
            variables: {
              input
            },
            update: updateCache(
              param,
              selectedClassification,
              classificationTarget
            ),
            awaitRefetchQueries: false,
            refetchQueries: ['timeSignalsCount', 'timeSignalsTotals']
          });
        } catch (e) {
          setToastContext({
            showError: true,
            message: 'toast.note.failure'
          });
        }
      } while (result?.data?.updateTimeSignals?.cursor);

      if (classificationTarget && !loading && !error) {
        setToastContext({
          showSuccess: true,
          message: getMoveMessage(classificationTarget, args.note),
          values: {
            message: truncateMessage(args.note?.description ?? ''),
            count: `${selectedNoteIds.length}`
          }
        });
      }
    },
    [
      loading,
      error,
      selectedFilterFn,
      bulkUpdateTimeSignals,
      param,
      selectedClassification,
      id,
      setToastContext
    ]
  );

  const onMultiEditTimeSignals = useCallback(
    async (args: BulkUpdateArgs) => {
      let result = null;
      clearCachedDates(id);
      do {
        const input = getFilterInput({
          ...args,
          cursor: result?.data?.updateTimeSignals.cursor ?? null,
          selectedFilterFn
        });
        try {
          result = await bulkUpdateTimeSignals({
            variables: {
              input
            },
            awaitRefetchQueries: true,
            refetchQueries: [
              'timeSignals',
              'timeSignalsCount',
              'timeSignalsTotals'
            ]
          });
        } catch (e) {
          setToastContext({
            showError: true,
            message: 'toast.note.failure'
          });
        }
      } while (result?.data?.updateTimeSignals?.cursor);

      if (!loading && !error) {
        setToastContext({
          showSuccess: true,
          message: 'note.multiEdit.nNotesUpdated',
          values: {
            count: `${args.selectedNoteIds.length}`
          }
        });
      }
    },
    [
      loading,
      error,
      id,
      selectedFilterFn,
      bulkUpdateTimeSignals,
      setToastContext
    ]
  );

  return {
    onTimeSignalClassificationUpdate,
    onMultiEditTimeSignals,
    updateInProgress: loading
  };
};
